// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable no-prototype-builtins */
import RelatedQuoteLines from "@bg/App/Components/RfqQuoteDetails/CustomerViewRalatedQuoteLines";
import QuoteLineImageCarousel from "@bg/App/Components/RfqQuoteDetails/QuoteLineImageCarousel";
import setGroupWishListLineIsSelected from "@bg/App/Handlers/Pages/MyListDetails/SetGroupSelectedLines";
import deleteLines from "@bg/App/Handlers/RequestQuote/DeleteLines";
import deleteRelatedLine from "@bg/App/Handlers/RequestQuote/DeleteRelatedLine";
import updateLineInfo from "@bg/App/Handlers/RequestQuote/UpdateLineInfo";
import useAccessibleSubmit from "@bg/App/Hooks/useAccessibleSubmit";
import { linkIconStyleGenerator } from "@bg/App/SharedStyles/GeneralStyles";
import { CustomAppState } from "@bg/App/Store/Reducers";
import ProductAvailability from "@bg/Overrides/Components/ProductAvailability";
import ProductDescription from "@bg/Overrides/Components/ProductDescription";
import TwoButtonModal from "@bg/Overrides/Components/TwoButtonModal";
import { productListLineStyles } from "@bg/Overrides/Widgets/MyListsDetails/MyListDetailsProductListLineStyles";
import { parseBoolean } from "@bg/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateQuoteLine from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/UpdateQuoteLine";
import translate from "@insite/client-framework/Translate";
import { QuoteLineModel, QuoteModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import ProductPartNumbers from "@insite/content-library/Components/ProductPartNumbers";
import RfqQuoteDetailsQuotedPricing from "@insite/content-library/Widgets/RfqQuoteDetails/RfqQuoteDetailsQuotedPricing";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import Icon from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const QuoteLineQuantity = React.lazy(
    () =>
        import(`@${BLUEPRINT_NAME === "bp" ? "bg" : BLUEPRINT_NAME}/App/Components/RfqQuoteDetails/QuoteLineQuantity`),
);

interface OwnProps {
    quote: QuoteModel;
    quoteLine: QuoteLineModel;
    groupName: string;
    lines: QuoteLineModel[] | null;
    group: any;
    total: number;
    extendedStyles?: RfqQuoteDetailsProposedDetailsGridItemStyles;
}

const mapStateToProps = (state: CustomAppState) => ({
    selectedGroupLines: state.commonActions["selectedGroupLines"],
    isQuoteDisabled: state.commonActions.isQuoteDisabled,
    addOnsGroupName: (getSettingsCollection(state).customSettings as any).addOnsGroupName as string,
    showPricing: state.context.showPricing,
});

const mapDispatchToProps = {
    setGroupWishListLineIsSelected,
    updateLineInfo,
    updateQuoteLine,
    deleteLines,
    removeCartLine,
    deleteRelatedLine,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RfqQuoteDetailsProposedDetailsGridItemStyles {
    relatedItemsWrapper?: InjectableCss;
    quantityGridItem?: InjectableCss;
    quotedPricingGridItem?: InjectableCss;
    quoteButtonGrid?: InjectableCss;
    addNotes?: LinkPresentationProps;
    removeQuoteLineLink?: LinkPresentationProps;
    notesGridItem?: InjectableCss;
    textAreaField?: TextAreaProps;
}

export const rfqQuoteDetailsProposedDetailsGridItemStyles: RfqQuoteDetailsProposedDetailsGridItemStyles = {
    quantityGridItem: {
        css: css`
            min-width: calc((100% - 150px) / 3);
            display: flex;
            justify-content: center;
            @media (max-width: 600px) {
                width: 50%;
                justify-content: flex-start;
                order: 2;
            }
        `,
    },
    quotedPricingGridItem: {
        css: css`
            flex-grow: 1;
            @media (max-width: 600px) {
                width: 100%;
                order: 1;
            }
        `,
    },
    quoteButtonGrid: {
        css: css`
            min-width: 150px;
            display: flex;
            justify-content: flex-end;
            align-items: flex-start;
            @media (max-width: 600px) {
                width: 50%;
                min-width: unset;
                order: 3;
            }
        `,
    },
    notesGridItem: {
        css: css`
            width: 100%;
            margin: 10px 0 0;
            text-align: right;
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            @media (max-width: 481px) {
                flex-direction: column;
                align-items: flex-start;
            }
        `,
    },
    addNotes: linkIconStyleGenerator("Create", "13px", 14, "line-height: 18px;"),
    removeQuoteLineLink: linkIconStyleGenerator("Delete", "13px", 14, "line-height: 18px;"),
    relatedItemsWrapper: {
        css: css`
            &&&.last-item .related-item:last-child {
                border-bottom: 3px solid ${getColor("common.ui3")};
            }
        `,
    },
    textAreaField: {
        mergeCss: true,
        cssOverrides: {
            inputSelect: css`
                resize: none;
            `,
        },
    },
};

const styles = rfqQuoteDetailsProposedDetailsGridItemStyles;

const RfqQuoteDetailsProposedDetailsGridItem = ({
    quote,
    quoteLine,
    groupName,
    selectedGroupLines,
    setGroupWishListLineIsSelected,
    lines,
    group,
    updateLineInfo,
    updateQuoteLine,
    deleteLines,
    removeCartLine,
    deleteRelatedLine,
    total,
    isQuoteDisabled,
    addOnsGroupName,
}: Props) => {
    const [lineGroup, setLineGroup] = useState<any>();
    const [lineGroups, setLineGroups] = useState<any>();
    const [relatedItems, setRelatedItems] = useState<QuoteLineModel[]>([]);
    const [notes, setNotes] = useState("");
    const [lineImages, setLineImages] = useState<any>();
    const [deleteLineModalIsOpen, setDeleteLineModalIsOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showNotes, setShowNotes] = useState(false);

    const toasterContext = useContext(ToasterContext);
    const dispatch = useDispatch();

    useEffect(() => {
        if (quoteLine && quoteLine?.properties && quoteLine?.properties?.groups) {
            setLineGroups(JSON.parse(quoteLine.properties.groups));
            setLineGroup(
                JSON.parse(quoteLine.properties.groups)?.filter(
                    (x: any) => x.groupName === groupName.toLowerCase(),
                )?.[0],
            );
        }
        if (quoteLine && quoteLine?.properties) {
            let images = [] as any;
            if (quoteLine?.properties && quoteLine?.properties?.images) {
                images = JSON.parse(quoteLine.properties["images"]);
            } else {
                images = [{ id: quoteLine.productId, imagePath: quoteLine.smallImagePath }];
            }
            setLineImages(images);
        }
    }, [groupName, quoteLine]);

    useEffect(() => {
        if (lineGroup && lineGroup?.hasOwnProperty("relatedLinesIds") && lineGroup.relatedLinesIds.length && lines) {
            const erps: string[] = lineGroup.relatedLinesIds.split(",");
            const relatedProducts = lines.filter(
                line => erps.some(x => x === line.id) && line.properties.isRelatedProduct === "true",
            );
            setRelatedItems(relatedProducts);
        }
        if (lineGroup) {
            setNotes(lineGroup.notes);
            setShowNotes(!!lineGroup.notes);
        }
    }, [lineGroup, lines]);

    const selectChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setGroupWishListLineIsSelected({
            wishListLineIds: [quoteLine.id],
            groupName,
        });
    };

    const qtyOrderedSubmitHandler = (value: string) => {
        const inputQty = Number.parseInt(value, 10);
        if (
            Number.isNaN(inputQty) ||
            (quoteLine.properties.minimumQty && Number(value) < Number(quoteLine.properties.minimumQty))
        ) {
            return;
        }

        dispatch({
            type: "Pages/xRfqQuoteDetailsProposed/UpdateProductsQty",
            lineModel: {
                ...quoteLine,
                properties: {
                    ...quoteLine.properties,
                    originalGroupName: groupName,
                },
                qtyOrdered: inputQty === 0 ? 1 : inputQty,
                originalQty: lineGroup.qtyOrdered,
            } as QuoteLineModel,
        });
    };

    const lineQty = React.useMemo(() => {
        return quoteLine.properties.isFixedQty === "true"
            ? quoteLine.properties.exactQty
            : quoteLine.properties.minimumQty && +lineGroup?.qtyOrdered < +quoteLine.properties.minimumQty
            ? quoteLine.properties.minimumQty
            : lineGroup?.qtyOrdered;
    }, [
        lineGroup?.qtyOrdered,
        quoteLine.properties.exactQty,
        quoteLine.properties.isFixedQty,
        quoteLine.properties.minimumQty,
    ]);

    const {
        value: qtyOrdered,
        changeHandler: qtyOrderedChangeHandler,
        keyDownHandler: qtyOrderedKeyDownHandler,
        blurHandler: qtyOrderedBlurHandler,
    } = useAccessibleSubmit(lineQty, qtyOrderedSubmitHandler, 0, Number(quoteLine.properties.minimumQty));
    const toggleNotesClickHandler = () => {
        setShowNotes(!showNotes);
    };

    const notesChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(event.currentTarget.value);
    };

    const notesBlurHandler = (e: any) => {
        if (e.currentTarget.value === lineGroup.notes) {
            return;
        }
        setShowNotes(e.currentTarget.value);
        updateLineInfo({
            cartId: quote.id,
            originalGroupName: groupName,
            linesModel: [
                {
                    lineId: quoteLine.id,
                    qtyOrdered: lineGroup.qtyOrdered,
                    sortOrder: lineGroup.sortOrder,
                    notes,
                },
            ],
            reloadQuote: true,
        });
    };

    const removeQuoteLine = () => {
        setIsDeleting(true);

        if (relatedItems.length) {
            const linesModel = [] as any;
            relatedItems.forEach(line => {
                const relatedLine = {
                    lineId: line.id,
                    parentId: quoteLine.id,
                };
                linesModel.push(relatedLine);
            });
            deleteRelatedLine({
                cartId: quote.id,
                originalGroupName: groupName,
                linesModel,
                onComplete: (result: any) => {
                    if (result && result.successful) {
                        deleteLine();
                    }
                },
            });
        } else {
            deleteLine();
        }
    };

    const deleteLine = () => {
        if (lineGroups.length === 1 && lineGroups[0].groupName.toLowerCase() === groupName.toLowerCase()) {
            removeCartLine({
                cartId: quote.id,
                cartLineId: quoteLine.id,
                reloadQuote: true,
                isQuoteLine: true,
            } as any);
            setIsDeleting(false);
            setDeleteLineModalIsOpen(false);
        } else {
            deleteLines({
                cartId: quote.id,
                originalGroupName: groupName,
                newGroupName: "",
                linesModel: [{ lineId: quoteLine.id }],
                reloadCart: false,
                reloadQuote: true,
                onComplete: result => {
                    setIsDeleting(false);
                    setDeleteLineModalIsOpen(false);
                },
            });
        }
    };

    const toggleNotesHeading = showNotes
        ? translate("Hide Notes")
        : !lineGroup?.notes || lineGroup?.notes.length === 0
        ? translate("Add Notes")
        : translate("Show Notes");

    const allowRemoveAddOns =
        quote.properties["allowAddOnsItemRemoval"].toLowerCase() === "true" &&
        groupName.toLowerCase() === addOnsGroupName.toLowerCase();

    return (
        <StyledWrapper>
            <StyledWrapper
                {...productListLineStyles.lineInnerContainer}
                className={`no-borders ${relatedItems.length > 0 ? "has-related-products" : ""} ${
                    lineGroup?.sortOrder === group.length ? "last-line" : ""
                }`}
                data-test-selector={`quoteLine_${quoteLine.productId}_${quoteLine.unitOfMeasure}`}
            >
                <StyledWrapper {...productListLineStyles.leftSection}>
                    <StyledWrapper {...productListLineStyles.imageGridItem}>
                        {!parseBoolean(quote.properties.isExpired) && (
                            <StyledWrapper {...productListLineStyles.dragGrid}>
                                {isQuoteDisabled &&
                                    !isQuoteDisabled.isDisabled &&
                                    !quote.isSalesperson &&
                                    groupName.toLowerCase() !== addOnsGroupName.toLowerCase() && (
                                        <>
                                            <Checkbox
                                                {...productListLineStyles.selectedCheckbox}
                                                checked={
                                                    selectedGroupLines?.hasOwnProperty(groupName) &&
                                                    selectedGroupLines[groupName].indexOf(quoteLine.id) > -1
                                                }
                                                onChange={selectChangeHandler}
                                                data-test-selector="selectItem"
                                            />
                                            <StyledWrapper {...productListLineStyles.dragIconWrapper}>
                                                <Icon
                                                    {...productListLineStyles.dragHandleIcon}
                                                    className="drag-handle"
                                                />
                                            </StyledWrapper>
                                        </>
                                    )}
                            </StyledWrapper>
                        )}
                        <QuoteLineImageCarousel
                            productId={quoteLine.id}
                            productImages={lineImages}
                            productUrl={quoteLine.productUri}
                        />
                    </StyledWrapper>

                    <StyledWrapper {...productListLineStyles.descriptionGridItem}>
                        <ProductDescription
                            product={quoteLine}
                            lines={2}
                            lineHeight={22}
                            extendedStyles={productListLineStyles.productDescriptionStyles}
                        />
                        <StyledWrapper {...productListLineStyles.partNumbersGridItem}>
                            <ProductPartNumbers
                                productNumber={quoteLine.properties.sku || quoteLine.erpNumber}
                                manufacturerItem={quoteLine.manufacturerItem}
                                extendedStyles={productListLineStyles.productPartNumbersStyles}
                            />
                        </StyledWrapper>
                        <StyledWrapper {...productListLineStyles.availabilityGridItem}>
                            <ProductAvailability
                                productId={quoteLine.productId!}
                                availability={quoteLine.availability!}
                                unitOfMeasure={quoteLine.unitOfMeasure}
                                trackInventory={quoteLine.trackInventory}
                                extendedStyles={productListLineStyles.productAvailabilityStyles}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>

                <StyledWrapper {...productListLineStyles.rightSection}>
                    <StyledWrapper {...productListLineStyles.rightSectionTop}>
                        <StyledWrapper {...productListLineStyles.priceAndAvailabilityInnerContainer}>
                            <StyledWrapper {...styles.quotedPricingGridItem}>
                                <RfqQuoteDetailsQuotedPricing
                                    quote={quote}
                                    quoteLine={quoteLine}
                                    extendedStyles={{
                                        priceCell: {
                                            css: css`
                                                text-align: center;
                                                width: 50%;
                                                @media (max-width: 600px) {
                                                    text-align: right !important;
                                                }
                                            `,
                                        },
                                    }}
                                />
                            </StyledWrapper>
                            <StyledWrapper {...styles.quantityGridItem}>
                                <React.Suspense fallback="...">
                                    {parseBoolean(quote.properties.isExpired) ? (
                                        qtyOrdered
                                    ) : (
                                        <QuoteLineQuantity
                                            qtyOrdered={qtyOrdered}
                                            quoteLine={quoteLine}
                                            quote={quote}
                                            isDisabled={isQuoteDisabled && isQuoteDisabled.isDisabled}
                                            qtyOrderedChangeHandler={qtyOrderedChangeHandler}
                                            qtyOrderedKeyDownHandler={qtyOrderedKeyDownHandler}
                                            qtyOrderedBlurHandler={qtyOrderedBlurHandler}
                                            onSubmit={qtyOrderedSubmitHandler}
                                        />
                                    )}
                                </React.Suspense>
                            </StyledWrapper>
                            <StyledWrapper {...styles.quoteButtonGrid}>
                                <Typography {...productListLineStyles.priceTotal}>
                                    <legend>{translate("Total")}</legend>
                                    <LocalizedCurrency amount={total || 0} />
                                </Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.notesGridItem}>
                        {allowRemoveAddOns && isQuoteDisabled && !isQuoteDisabled.isDisabled ? (
                            <Link {...styles.removeQuoteLineLink} onClick={() => setDeleteLineModalIsOpen(true)}>
                                {translate("Remove Item")}
                            </Link>
                        ) : (
                            <StyledWrapper></StyledWrapper>
                        )}
                        {isQuoteDisabled && !isQuoteDisabled.isDisabled && !parseBoolean(quote.properties.isExpired) && (
                            <Link {...styles.addNotes} onClick={toggleNotesClickHandler}>
                                {toggleNotesHeading}
                            </Link>
                        )}
                        {showNotes && (
                            <TextArea
                                {...styles.textAreaField}
                                value={notes}
                                onChange={notesChangeHandler}
                                onBlur={notesBlurHandler}
                                rows={3}
                                maxLength={300}
                                data-test-selector="cartline_notes"
                                disabled={!quote.canModifyOrder}
                            />
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            {relatedItems && relatedItems.length > 0 && (
                <StyledWrapper
                    {...styles.relatedItemsWrapper}
                    className={lineGroup.sortOrder === group.length ? "last-item" : ""}
                >
                    {relatedItems &&
                        relatedItems.length > 0 &&
                        relatedItems.map(line => (
                            <RelatedQuoteLines
                                key={`relatedQuoteLine_${line.id}`}
                                quote={quote}
                                parentLineId={quoteLine.id}
                                groupName={groupName}
                                quoteLine={line}
                                isDisabled={isQuoteDisabled && isQuoteDisabled.isDisabled}
                                editable={!parseBoolean(quote?.properties.isExpired)}
                            />
                        ))}
                </StyledWrapper>
            )}
            <TwoButtonModal
                modalIsOpen={deleteLineModalIsOpen}
                headlineText={translate("Delete List Item")}
                messageText={`${translate("Are you sure you want to delete this item?")}`}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={() => setDeleteLineModalIsOpen(false)}
                onSubmit={removeQuoteLine}
                submitTestSelector="delteRelatedQuoteLine"
                showSubmitBtnLoadingOn={isDeleting}
            />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(RfqQuoteDetailsProposedDetailsGridItem);
